import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { SegmentedButton } from '@/components/SegmentedButton'
import { RoutePath } from '@/RoutePath'

import ProfileAddressPage from './components/ProfileAddressPage'
import ProfileAffiliatePage from './components/ProfileAffiliatePage'
import ProfileEditPage from './components/ProfileEditPage'

type TabValue = 'personal' | 'addresses' | 'affiliate'

const tabToRoute: Record<TabValue, string> = {
  personal: RoutePath.ClientProfilePersonal,
  addresses: RoutePath.ClientProfileAddresses,
  affiliate: RoutePath.ClientProfileAffiliate,
}

export default function ProfilePage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Determine active tab from route
  const getActiveTab = (): TabValue => {
    if (location.pathname === RoutePath.ClientProfile || location.pathname === RoutePath.ClientProfilePersonal) {
      return 'personal'
    }
    if (location.pathname === RoutePath.ClientProfileAddresses) {
      return 'addresses'
    }
    if (location.pathname === RoutePath.ClientProfileAffiliate) {
      return 'affiliate'
    }
    return 'personal'
  }

  const activeTab = getActiveTab()

  // Redirect base profile route to personal tab
  useEffect(() => {
    if (location.pathname === RoutePath.ClientProfile) {
      navigate(RoutePath.ClientProfilePersonal, { replace: true })
    }
  }, [location.pathname, navigate])

  const { t } = useTranslation()
  const tabOptions = [
    { value: 'personal', label: t('profile.personalDetails') },
    { value: 'addresses', label: t('profile.addresses') },
    { value: 'affiliate', label: t('profile.affiliate') },
  ]

  const handleTabChange = (value: string) => {
    const tab = value as TabValue
    navigate(tabToRoute[tab])
  }

  return (
    <div className='mx-auto max-w-2xl space-y-6 p-6 px-2 md:px-6' dir='rtl'>
      <div className='mb-2'>
        <h1 className='text-text-primary mt-2 text-2xl font-medium'>{t('profile.myProfileTitle')}</h1>
        <p className='text-text-secondary mt-1 text-sm'>{t('profile.myProfileSubtitle')}</p>
      </div>

      <SegmentedButton options={tabOptions} value={activeTab} onChange={handleTabChange} className='w-full' />

      {/* Personal Details Tab */}
      {activeTab === 'personal' && <ProfileEditPage />}

      {/* Addresses Tab */}
      {activeTab === 'addresses' && <ProfileAddressPage />}

      {/* Affiliate Marketing Tab */}
      {activeTab === 'affiliate' && <ProfileAffiliatePage />}
    </div>
  )
}
